import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Prompt, Text, useDisclosure } from '@components/mantine';
import { AuthenticationEvents } from '@core/authentication';
import { Translation } from '@core/locales';
import { OrganizationSelectors } from '@core/organization';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import ms from 'ms';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Locales } from '../../strings/Locales.js';
import { getThrottle } from '../../utils/GetThrottle.js';
import { getFormatedTimeUntilLogout } from '../../utils/GetFormatedTimeUntilLogout.js';
dayjs.extend(duration);
var OPEN_PROMPT_DELAY = ms('25m');
var PROMPT_LOGOUT_DELAY = ms('5m');
// cannot use React.useState for this one since many write are happening too fast
var promptTimerId = -1;
var getIdleLogoutDelay = function getIdleLogoutDelay(adminUITimeoutInSeconds) {
    return adminUITimeoutInSeconds ? adminUITimeoutInSeconds * 1000 : OPEN_PROMPT_DELAY;
};
var useBroadcastData = function useBroadcastData(param) {
    var id = param.id, onChange = param.onChange;
    var generatedId = useMemo(function() {
        return performance.now();
    }, []);
    var broadcastUpdate = function broadcastUpdate(value) {
        localStorage.setItem(id, JSON.stringify({
            value: value,
            sourceId: generatedId
        }));
    };
    var onLocalStorageUpdate = function onLocalStorageUpdate(ev) {
        if (ev.key === id && ev.newValue) {
            var parsed = JSON.parse(ev.newValue);
            if ((parsed === null || parsed === void 0 ? void 0 : parsed.sourceId) !== generatedId) {
                onChange();
            }
        }
    };
    useEffect(function() {
        window.addEventListener('storage', onLocalStorageUpdate);
        return function() {
            window.removeEventListener('storage', onLocalStorageUpdate);
        };
    }, []);
    return [
        broadcastUpdate
    ];
};
var dispatchFullLogoutEvent = function dispatchFullLogoutEvent() {
    var event = new Event(AuthenticationEvents.FullLogout, {
        bubbles: true
    });
    document.dispatchEvent(event);
};
var ActualIdleLogoutModel = function ActualIdleLogoutModel() {
    var _useDisclosure = _sliced_to_array(useDisclosure(false), 2), opened = _useDisclosure[0], _useDisclosure_ = _useDisclosure[1], open = _useDisclosure_.open, close = _useDisclosure_.close;
    var _useState = _sliced_to_array(useState(-1), 2), logoutIntervalId = _useState[0], setLogoutIntervalId = _useState[1];
    var _useState1 = _sliced_to_array(useState(null), 2), logoutAt = _useState1[0], setLogoutAt = _useState1[1];
    var adminUITimeoutInSeconds = useSelector(OrganizationSelectors.getOrganization).adminUITimeoutInSeconds;
    var openPromptDelay = getIdleLogoutDelay(adminUITimeoutInSeconds);
    var checkTimer = function checkTimer1() {
        if (logoutAt !== null && Date.now() >= logoutAt) {
            dispatchFullLogoutEvent();
        } else {
            var id = window.setTimeout(checkTimer, 1000);
            setLogoutIntervalId(id);
        }
    };
    var stay = function stay() {
        close();
        onUserActivity();
        clearTimeout(logoutIntervalId);
        setLogoutAt(null);
    };
    var openModalIfNeeded = function openModalIfNeeded() {
        if (!opened) {
            open();
            setLogoutAt(Date.now() + PROMPT_LOGOUT_DELAY);
        }
    };
    var resetTimer = function resetTimer() {
        clearTimeout(promptTimerId);
        promptTimerId = window.setTimeout(openModalIfNeeded, openPromptDelay);
    };
    var _useBroadcastData = _sliced_to_array(useBroadcastData({
        id: 'last-activity',
        onChange: function onChange() {
            if (!document.hasFocus()) {
                stay();
            }
        }
    }), 1), update = _useBroadcastData[0];
    var onUserActivity = useMemo(function() {
        return getThrottle(function() {
            resetTimer();
            if (document.hasFocus()) {
                update(dayjs().toJSON());
            }
        }, 1000);
    }, []);
    useEffect(function() {
        window.onmousemove = onUserActivity;
        window.onmousedown = onUserActivity; // catches touchscreen presses as well
        window.ontouchstart = onUserActivity; // catches touchscreen swipes as well
        window.onclick = onUserActivity; // catches touchpad clicks as well
        window.onkeydown = onUserActivity;
        window.addEventListener('scroll', onUserActivity, true);
        resetTimer();
    }, []);
    useEffect(function() {
        if (logoutAt !== null) {
            checkTimer();
        }
    }, [
        logoutAt,
        setLogoutIntervalId
    ]);
    var now = dayjs();
    var timeUntilAutomaticLogout = dayjs.duration(dayjs(logoutAt).diff(now));
    return /*#__PURE__*/ _jsxs(Prompt.Warning, {
        id: "idle-logout-modal",
        opened: opened,
        title: Locales.format('IdleLogoutPromptTitle'),
        onClose: function onClose() {
            return stay();
        },
        children: [
            /*#__PURE__*/ _jsx(Translation, {
                t: Locales,
                i18nKey: "IdleLogoutPromptDescription",
                options: {
                    time: getFormatedTimeUntilLogout(timeUntilAutomaticLogout)
                },
                children: /*#__PURE__*/ _jsx(Text, {
                    inherit: true,
                    span: true,
                    fw: 500
                })
            }),
            /*#__PURE__*/ _jsxs(Prompt.Footer, {
                children: [
                    /*#__PURE__*/ _jsx(Prompt.CancelButton, {
                        onClick: dispatchFullLogoutEvent,
                        children: Locales.format('IdleLogoutPromptLogout')
                    }),
                    /*#__PURE__*/ _jsx(Prompt.ConfirmButton, {
                        onClick: stay,
                        children: Locales.format('IdleLogoutPromptStay')
                    })
                ]
            })
        ]
    });
};
export var IdleLogoutModal = function IdleLogoutModal() {
    var adminUITimeoutInSeconds = useSelector(OrganizationSelectors.getOrganization).adminUITimeoutInSeconds;
    if (adminUITimeoutInSeconds === undefined) {
        return null;
    }
    return /*#__PURE__*/ _jsx(ActualIdleLogoutModel, {});
};

//# sourceMappingURL=IdleLogoutModal.js.map